"use client";

import { useMemo } from "react";
import type { ProductRef } from "../types";
import { SearchableTopicPills } from "./searchable-topic-pills";
import { mergeTopicItems, type TopicItem } from "./topic-items";
import { useBrowserSearchParams } from "./use-browser-search-params";
import { useTagToggle } from "./use-tag-toggle";

interface SidebarTopicEntry {
  count?: number;
  displayName: string;
  slug: string;
}

interface SidebarTopicsProps {
  activeTags?: string[];
  categoryName?: string;
  initialSearch?: string;
  tagDisplayNames?: Map<string, string>;
  tags: SidebarTopicEntry[];
  tools: ProductRef[];
}

export function SidebarTopics({
  activeTags,
  categoryName,
  initialSearch,
  tagDisplayNames,
  tags,
  tools,
}: SidebarTopicsProps) {
  const searchParams = useBrowserSearchParams({ initialSearch });
  const activeSet = useMemo(() => new Set(activeTags), [activeTags]);
  const { toggleTag } = useTagToggle({ searchParams });

  const topicItems = useMemo(() => {
    const baseItems: TopicItem[] = [
      ...tags.map((tag) => ({
        id: tag.slug,
        label: tag.displayName,
        kind: "tag" as const,
        count: tag.count,
      })),
      ...tools.map((tool) => ({
        id: tool.slug,
        label: tool.displayName,
        kind: "tool" as const,
      })),
    ];

    // Inject URL-active tags that aren't already in the trending/sidebar list
    // so the sidebar always reflects the current URL filter state.
    const existingIds = new Set(baseItems.map((item) => item.id));
    for (const slug of activeTags ?? []) {
      if (!existingIds.has(slug)) {
        baseItems.push({
          id: slug,
          label: tagDisplayNames?.get(slug) ?? slug,
          kind: "tag" as const,
        });
      }
    }

    return mergeTopicItems({ items: baseItems }).map((item) => ({
      ...item,
      selected: activeSet.has(item.id),
      onSelect: () => toggleTag(item.id),
    }));
  }, [activeSet, activeTags, tagDisplayNames, tags, toggleTag, tools]);

  const title = categoryName ? `Topics in ${categoryName}` : "Topics";

  return (
    <div className="p-0" style={{ fontFamily: "var(--homepage-sans-stack)" }}>
      <div className="mb-3 font-semibold text-[0.7rem] text-[var(--explore-text-muted)] uppercase tracking-[0.1em]">
        {title}
      </div>
      <SearchableTopicPills
        emptyState="No tags or tools are available right now."
        initialVisible={12}
        items={topicItems}
      />
    </div>
  );
}
